import { motion } from 'motion/react';

/**
 * MetricCard + Metric — a metrics-forward engagement card.
 *
 * A generic dark card shell that leads with a big gradient stat. Content is
 * passed as children so the primitive stays content-agnostic; the section
 * composes the industry label, title, body and pull-quote. Status drives a
 * tone-coloured left accent (amber = in build, green = shipped). Entrance
 * (scroll-into-view, index-staggered via `delay`) and hover motion via
 * `motion`; wrap the group in <MotionConfig reducedMotion="user"> for a11y.
 */

const ACCENT = {
  'In build': 'bg-feedback-warn/40',
  Shipped: 'bg-feedback-success/40',
};

const cardVariants = (delay) => ({
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1], delay } },
});

/** Big gradient headline stat. `value` may be a number or a short phrase. */
export function Metric({ value, unit, label }) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="font-display font-semibold leading-none text-metric md:text-stat bg-brand-gradient bg-clip-text text-transparent">
          {value}
        </span>
        {unit && <span className="font-display text-h4 text-ink-200">{unit}</span>}
      </div>
      {label && <p className="mt-2.5 text-body-sm text-ink-300 m-0 max-w-prose">{label}</p>}
    </div>
  );
}

/** Card shell. Pass any content as children. */
export function MetricCard({ status, featured = false, delay = 0, className = '', children }) {
  const accent = ACCENT[status] ?? 'bg-white/20';
  return (
    <motion.article
      variants={cardVariants(delay)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
      className={
        'group relative overflow-hidden rounded-xl bg-white/[.025] border border-white/[.08] ' +
        (featured ? 'p-7 md:p-10 ' : 'p-6 md:p-7 ') +
        'transition-colors duration-200 hover:bg-white/[.04] hover:border-white/[.14] ' +
        className
      }
    >
      <span aria-hidden="true" className={'absolute inset-y-0 left-0 w-1 ' + accent} />
      {children}
    </motion.article>
  );
}
